import { useEffect, useMemo, useState } from "react";
import { getData } from "../../api/ApiRest";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    plugins,
} from "chart.js";
import LoadingComponent from "../../widgets/LoadingComponent";
import { Grid, Paper, Typography } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { formatDateddMMMyyyy } from "../../widgets/DateTime";
import { formatNumber } from "../../widgets/FormatNumber";
import { Button, Modal } from "react-bootstrap";
import { FaCheck, FaEye, FaStreetView } from "react-icons/fa";

// Registrar módulos de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

function EmisionDashboard() {
    const [loading, setLoading] = useState(false);
    const [totales, setTotales] = useState([]); ///Totales por año
    const [masEmision, setMasEmision] = useState([]); ///State para saber en que fecha hubo mas emisión
    const [anios_x, setAnios_x] = useState([]); ///State para tomar solamente los años
    const [promedios, setPromedios] = useState([]); ///State para tomar los promedios por mes/año
    const [dias, setDias] = useState([]); ///state para tomar los promedios por dia/año

    const [modalRechazado, setModalRechazado] = useState({
        ModalVisible: false, Titulo: ''
    });

    const [modalCufe, setModalCufe] = useState({
        ModalVisible: false, Titulo: ''
    });

    const [rechazados, setRechazados] = useState([]);
    const [cufeErrado, setCufeErrado] = useState([]);

    const labels = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre"
    ];

    const chartData = useMemo(() => {
        const anios = [...new Set(totales.map(v => v.yyyy))];

        const datasets = anios.map((anio, index) => {
            const dataxAnio = Array(12).fill(0);

            totales.forEach(v => {
                if (v.yyyy === anio)
                    dataxAnio[v.mmm - 1] = v.total_facturas_emitidas;
            })

            const colors = [
                "rgba(144, 203, 216, 1)",  // azul claro
                "rgba(34, 75, 106, 1)",  // azul oscuro
            ];

            return {
                label: anio.toString(),
                data: dataxAnio,
                backgroundColor: colors[index % colors.length]
            };
        });

        return {
            labels,
            datasets
        }
    }, [totales]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "bottom" },
            title: {
                display: true,
            }
        }
    }), []);

    useEffect(() => {
        let isMounted = true;

        ///Función para traer la info de los años para graficar en el dashboard
        const EmisionesGenerales = async () => {
            setLoading(true);

            await getData('api/Configuracion/Dashboard/EmisionesGenerales').then(response => {
                if (isMounted) {
                    setTotales(response.datosEmisiones);
                    setMasEmision(response.fechaMasEmision);
                    setPromedios(response.promedioYyyy);
                    setDias(response.promedioDia);
                    setRechazados(response.facturasRechazadas);
                    setCufeErrado(response.cufeErrado);

                    ///Tomamos solamente los años que se visualicen
                    const anios = [...new Set(response.datosEmisiones.map(v => v.yyyy))];
                    setAnios_x(anios);
                }
                setLoading(false);
            }, error => {
                console.log(error);
                setLoading(false);
            })
        }

        EmisionesGenerales();

        return () => {
            isMounted = false;
        };
    }, [])

    ///Función para validar por mes/año el total de los folios que se han realizado, mas que todo consumo
    function facturacionFolios() {
        const resultado = labels.map((mes, i) => {
            let fila = { Mes: mes };
            anios_x.forEach((anio) => {
                fila[anio] = 0;
            });

            return fila;
        })

        totales.forEach((v) => {
            const medIndex = v.mmm - 1;
            resultado[medIndex][v.yyyy] = v.total_facturas_emitidas;
        })

        return resultado;
    }

    const cambiarEstadoFactura = (id) => {
        setLoading(true);

        getData('api/Configuracion/Dashboard/cambiarEstadoFactura', {
            id: id
        }).then(response => {
            if (response.status){
                alert(response.msj);
            }

            setLoading(false);
        }, error => {
            console.log(error);
            setLoading(false);
        });
    }

    return (
        <Grid container spacing={2} padding={2}>
            <LoadingComponent Loading={loading} />
            <Grid item xs={12} md={6}>
                <Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
                    <div className="row" style={{ textAlign: 'center', justifyContent: 'center' }}>
                        <div className="col-md-10">
                            <Typography variant="h5" className="text-center">
                                Emisión mes/año
                            </Typography>
                        </div>
                        <div className="col-md-1">
                            <Button variant="primary" type='button' onClick={() => {
                                setModalCufe({
                                    ModalVisible: true,
                                    Titulo: 'Facturas cufe errado'
                                });
                            }}>
                                <FaStreetView size={'1rem'} />
                            </Button>
                        </div>
                        <div className="col-md-1">
                            <Button variant="warning" type='button' onClick={() => {
                                setModalRechazado({
                                    ModalVisible: true,
                                    Titulo: 'Clientes con mas rechazos'
                                });
                            }}>
                                <FaEye size={'1rem'} />
                            </Button>
                        </div>
                    </div>
                    <Typography variant="body2">
                        <div style={{ height: "300px" }}>
                            <Bar data={chartData} options={options} />
                        </div>
                    </Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
                    <Typography variant="h5" className="text-center">
                        Top 5 fechas con mas emisión
                    </Typography>
                    <Typography variant="body2">
                        {masEmision.length > 0 ?
                            <table className='table table-sm table-bordered table-hover'>
                                <thead>
                                    <tr>
                                        <th>Día</th>
                                        <th>Total emisión</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {masEmision.map((item, index) => (
                                        <tr key={index}>
                                            <td className="text-center">
                                                {formatDateddMMMyyyy(item.dia_mas_vendido)}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {formatNumber(item.total_dia_mas_vendido)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            : null
                        }
                    </Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
                    <Typography variant="h5" className="text-center">
                        Emisión detallada
                    </Typography>
                    <Typography variant="body2">
                        <table className='table table-sm table-bordered table-hover'>
                            <thead>
                                <tr>
                                    <th>Mes</th>
                                    {anios_x.map((item, index) => (
                                        <th key={index}>{item}</th>
                                    ))}
                                    <th>% Crec</th>
                                </tr>
                            </thead>
                            <tbody>
                                {facturacionFolios().map((item, index) => {
                                    const anios = anios_x.map((anio) => item[anio] || 0);

                                    const first = anios[0];
                                    const last = anios[anios.length - 1];
                                    const crecimiento = first > 0 ? ((last - first) / first) * 100 : "100";

                                    return (
                                        <tr key={index}>
                                            <td>{item.Mes}</td>
                                            {anios_x.map((anio, i) => (
                                                <td style={{ textAlign: 'right' }} key={i}>{formatNumber(item[anio])}</td>
                                            ))}
                                            <td style={{ textAlign: 'right' }}>{formatNumber(crecimiento)}%</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>Total</th>
                                    {anios_x.map((anio, i) => (
                                        <th style={{ textAlign: 'right' }} key={i}>{formatNumber(totales.filter(item => item.yyyy === anio).reduce((acc, curr) => acc + curr.total_facturas_emitidas, 0))}</th>
                                    ))}
                                    <th></th>
                                </tr>
                            </tfoot>
                        </table>
                    </Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={3} style={{ padding: '20px', height: '100%' }}>
                    <Typography variant="h5" className="text-center">
                        Promedio emisión mensual
                    </Typography>
                    <Typography variant="body2">
                        {promedios.length > 0 ?
                            <table className='table table-sm table-bordered table-hover'>
                                <thead>
                                    <tr>
                                        <th>Año</th>
                                        <th>Prom mensual</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promedios.map((item, index) => (
                                        <tr key={index}>
                                            <td className="text-center">
                                                {item.yyyy}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {formatNumber(item.promedio)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            : null}
                    </Typography>

                    <br />
                    <Typography variant="h5" className="text-center">
                        Promedio emisión día
                    </Typography>
                    <Typography variant="body2">
                        {dias.length > 0 ?
                            <table className='table table-sm table-bordered table-hover'>
                                <thead>
                                    <tr>
                                        <th>Año</th>
                                        <th>Prom dias</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dias.map((item, index) => (
                                        <tr key={index}>
                                            <td className="text-center">
                                                {item.yyyy}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {formatNumber(item.promedio)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            : null}
                    </Typography>
                </Paper>
            </Grid>

            <Modal
                show={modalRechazado.ModalVisible}
                size="lg"
                onHide={() => {
                    setModalRechazado({
                        ModalVisible: false, Titulo: ''
                    });
                }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{modalRechazado.Titulo}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className='table-responsive'>
                            <table className='table table-borderless table-hover'>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                        <th className="estilo-encabezado" style={{ fontSize: 13 }}>Cliente</th>
                                        <th className="estilo-encabezado" style={{ fontSize: 13 }}>Rechazos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rechazados.map((item, i) => (
                                        <tr key={i}>
                                            <td className="text-center" style={{ fontSize: 13 }}>
                                                {item.cliente}
                                            </td>
                                            <td className="text-center" style={{ fontSize: 13 }}>
                                                {formatNumber(item.cantidad)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                show={modalCufe.ModalVisible}
                size="lg"
                onHide={() => {
                    setModalCufe({
                        ModalVisible: false, Titulo: ''
                    });
                }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{modalCufe.Titulo}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className='table-responsive'>
                            <table className='table table-borderless table-hover'>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                        <th className="estilo-encabezado" style={{ fontSize: 13 }}>Identificación</th>
                                        <th className="estilo-encabezado" style={{ fontSize: 13 }}>Factura</th>
                                        <th className="estilo-encabezado"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cufeErrado.map((item, i) => (
                                        <tr key={i}>
                                            <td className="text-center" style={{ fontSize: 13 }}>
                                                {item.identificacion}
                                            </td>
                                            <td className="text-center" style={{ fontSize: 13 }}>
                                                {item.factura}
                                            </td>
                                            {item.estado !== true ? <td className="text-center" style={{ fontSize: 13 }}>
                                                <button type="button" onClick={() => cambiarEstadoFactura(item.id)} className="btn btn-sm btn-success">
                                                    <FaCheck />
                                                </button>
                                            </td> : null}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </Grid>
    )
}

export default EmisionDashboard;